import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2, RefreshCw, Search, ShieldAlert, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const StatusBadge = ({ ok, code }) => {
  if (ok === true) {
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="email-status-ok">accepted · {code ?? '—'}</Badge>;
  }
  return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid="email-status-fail">failed · {code ?? '—'}</Badge>;
};

const TYPE_OPTS = ['', 'invitation', 'temp_password', 'password_reset_link', 'schedule', 'cancellation'];

export default function AdminEmailDiagnosticsPage() {
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState(null);
  const [log, setLog] = useState(null);
  const [days, setDays] = useState(7);
  const [emailFilter, setEmailFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const loadDiagnostics = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-diagnostics');
      setDiagnostics(r.data);
    } catch (e) {
      toast.error('Failed to load email diagnostics');
    }
  }, []);

  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: '200' });
      if (emailFilter) params.set('email', emailFilter);
      if (typeFilter) params.set('email_type', typeFilter);
      const r = await api.get(`/admin/email-log?${params.toString()}`);
      setLog(r.data);
    } catch (e) {
      toast.error('Failed to load email log');
    } finally {
      setLoading(false);
    }
  }, [days, emailFilter, typeFilter]);

  useEffect(() => { loadDiagnostics(); loadLog(); }, [loadDiagnostics, loadLog]);

  const domainAuthed = diagnostics?.sender_domain_authenticated;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="email-diagnostics-page">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Button variant="outline" size="sm" onClick={() => { loadDiagnostics(); loadLog(); }} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Email Diagnostics</h1>
        <p className="text-sm text-slate-500 mt-1">
          Inspect transactional email deliverability and see every invite / temp-password / schedule message AbbeyZone has tried to send.
        </p>
      </header>

      {/* Configuration card */}
      <Card className="p-5" data-testid="config-card">
        <h2 className="font-semibold text-slate-800 mb-3">Sender Configuration</h2>
        {!diagnostics ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">From:</span> <span className="font-mono">{diagnostics.sender?.from_email}</span></div>
            <div><span className="text-slate-500">Display name:</span> {diagnostics.sender?.from_name}</div>
            <div><span className="text-slate-500">Reply-To:</span> <span className="font-mono">{diagnostics.sender?.reply_to}</span></div>
            <div><span className="text-slate-500">Sender domain:</span> <span className="font-mono">{diagnostics.sender?.sender_domain}</span></div>
            <div><span className="text-slate-500">SendGrid API key:</span> {diagnostics.sendgrid_api_configured ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">configured</Badge> : <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">missing</Badge>}</div>
            <div><span className="text-slate-500">Transactional hardening:</span> {diagnostics.transactional_hardening_active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700">click-tracking off · List-Unsubscribe set</Badge> : '—'}</div>
            <div><span className="text-slate-500">Invitation validity:</span> {diagnostics.invitation_validity_days} days</div>
            <div><span className="text-slate-500">Temp password expiry:</span> {diagnostics.temp_password_expiry}</div>
          </div>
        )}
      </Card>

      {/* Domain auth card */}
      <Card className={`p-5 ${domainAuthed === false ? 'border-amber-300 bg-amber-50' : ''}`} data-testid="domain-auth-card">
        <div className="flex items-start gap-3">
          {domainAuthed === false
            ? <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            : <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />}
          <div className="flex-1">
            <h2 className="font-semibold text-slate-800">Domain Authentication (SPF / DKIM)</h2>
            <p className="text-sm text-slate-600 mt-1">
              {domainAuthed === false
                ? 'This is the #1 reason corporate recipients (Microsoft 365 / Gmail Workspace / Mimecast / Proofpoint) silently drop AbbeyZone invitation emails into spam. Fix this and deliverability typically jumps from ~60 % to ~98 %.'
                : domainAuthed === true
                  ? 'Sender domain is fully authenticated. Recipients can verify mail came from you, so corporate filters will trust it.'
                  : 'Status unknown — see error below.'}
            </p>
            {diagnostics?.authenticated_domains_error && (
              <p className="text-xs text-red-600 mt-2">SendGrid API call: {diagnostics.authenticated_domains_error}</p>
            )}
            {diagnostics?.authenticated_domains?.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="text-sm w-full">
                  <thead><tr className="text-left text-slate-500"><th className="py-1 pr-4">Domain</th><th className="py-1 pr-4">Subdomain</th><th className="py-1 pr-4">Valid</th></tr></thead>
                  <tbody>
                    {diagnostics.authenticated_domains.map((d, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-1 pr-4 font-mono">{d.domain}</td>
                        <td className="py-1 pr-4 font-mono">{d.subdomain || '—'}</td>
                        <td className="py-1 pr-4">{d.valid ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {diagnostics?.recommendations?.length > 0 && (
              <ul className="text-sm text-amber-800 mt-3 space-y-1 list-disc list-inside" data-testid="recommendations-list">
                {diagnostics.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
              </ul>
            )}
          </div>
        </div>
      </Card>

      {/* Email log */}
      <Card className="p-5" data-testid="email-log-card">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Window (days)</label>
            <select className="border rounded px-2 py-1 text-sm" value={days} onChange={e => setDays(parseInt(e.target.value))} data-testid="days-select">
              {[1, 7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <select className="border rounded px-2 py-1 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="type-select">
              {TYPE_OPTS.map(t => <option key={t} value={t}>{t || 'all'}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-slate-500 mb-1">Recipient (contains)</label>
            <Input value={emailFilter} onChange={e => setEmailFilter(e.target.value)} placeholder="e.g. jane@client.com" data-testid="email-filter-input" />
          </div>
          <Button onClick={loadLog} disabled={loading} data-testid="apply-filter-btn"><Search className="w-4 h-4 mr-2" /> Apply</Button>
        </div>

        {log?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Sends in window" value={log.stats.total} />
            <Stat label="Accepted by SendGrid" value={log.stats.sent} positive />
            <Stat label="Failed" value={log.stats.failed} negative={log.stats.failed > 0} />
            <Stat label="Unique recipients" value={log.stats.unique_recipients} />
          </div>
        )}

        <div className="overflow-x-auto border rounded">
          <table className="text-sm w-full" data-testid="email-log-table">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="py-2 px-3">Sent</th>
                <th className="py-2 px-3">Type</th>
                <th className="py-2 px-3">Recipient</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">SendGrid ID</th>
                <th className="py-2 px-3">Subject / error</th>
              </tr>
            </thead>
            <tbody>
              {(log?.rows || []).map(row => (
                <tr key={row.id} className="border-t" data-testid={`email-log-row-${row.id}`}>
                  <td className="py-2 px-3 whitespace-nowrap text-slate-600">{new Date(row.sent_at).toLocaleString()}</td>
                  <td className="py-2 px-3"><Badge variant="secondary">{row.email_type}</Badge></td>
                  <td className="py-2 px-3 font-mono">{row.to_email}</td>
                  <td className="py-2 px-3"><StatusBadge ok={row.success} code={row.status_code} /></td>
                  <td className="py-2 px-3 font-mono text-xs text-slate-500">{row.sendgrid_message_id || '—'}</td>
                  <td className="py-2 px-3 text-slate-700">{row.error_message || row.subject || '—'}</td>
                </tr>
              ))}
              {(!log || log.rows?.length === 0) && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">No email sends in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {log?.note && <p className="text-xs text-slate-500 mt-3 leading-relaxed">{log.note}</p>}
      </Card>
    </div>
  );
}

const Stat = ({ label, value, positive, negative }) => (
  <div className={`rounded-lg border p-3 ${negative ? 'bg-red-50 border-red-200' : positive ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-xl font-semibold">{value ?? 0}</div>
  </div>
);
